import React, { useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Loader2, CheckCircle2, AlertTriangle, Clock } from "lucide-react";

/**
 * Detail-Modal für PostFinance-Batches (sowohl pain.001 als auch pain.008-LSV).
 *
 * Props:
 *   open, onClose, companyId
 *   batchId            – DB-ID
 *   batchType          – "payment" (pain.001) | "lsv" (pain.008)
 */
export function BatchDetailModal({ open, onClose, companyId, batchId, batchType }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !batchId) return;
    setLoading(true);
    setData(null);
    const url =
      batchType === "lsv"
        ? `/companies/${companyId}/postfinance/lsv-collection-batches/${batchId}`
        : `/companies/${companyId}/postfinance/payment-batches/${batchId}`;
    apiClient
      .get(url)
      .then((r) => setData(r.data))
      .catch(() => setData({ error: true }))
      .finally(() => setLoading(false));
  }, [open, batchId, batchType, companyId]);

  const lines = data?.payments || data?.collections || [];
  const lineLabel = batchType === "lsv" ? "Einzüge" : "Zahlungen";

  const reconciliationBadge = (status) => {
    if (!status) return null;
    const map = {
      settled: ["bg-emerald-100 text-emerald-800", "abgewickelt"],
      partial: ["bg-amber-100 text-amber-800", "teilweise"],
      rejected: ["bg-rose-100 text-rose-800", "abgelehnt"],
      in_progress: ["bg-sky-100 text-sky-800", "in Verarbeitung"],
    };
    const [cls, label] = map[status] || ["bg-slate-200 text-slate-700", status];
    return <Badge className={cls}>{label}</Badge>;
  };

  const uploadIcon = (s) => {
    if (s === "uploaded") return <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />;
    if (s === "upload_failed") return <AlertTriangle className="h-3.5 w-3.5 text-rose-600" />;
    return <Clock className="h-3.5 w-3.5 text-slate-500" />;
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="batch-detail-modal"
      >
        <DialogHeader>
          <DialogTitle>
            {batchType === "lsv" ? "LSV-Lastschrift-Lauf" : "Zahlungs-Lauf"}
          </DialogTitle>
          <DialogDescription>
            {data?.msg_id || (loading ? "Lädt …" : "Details")}
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-6">
            <Loader2 className="h-4 w-4 animate-spin" /> Lädt Batch-Details …
          </div>
        )}

        {data?.error && (
          <div className="text-sm text-rose-700">Batch nicht gefunden.</div>
        )}

        {data && !data.error && (
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
              <Field label="Filename" value={data.filename} mono />
              <Field
                label="Total"
                value={`${(data.total_amount || 0).toFixed(2)} ${data.currency || "CHF"}`}
              />
              <Field
                label="Anzahl"
                value={`${data.payment_count ?? data.collection_count ?? 0} ${lineLabel}`}
              />
              <Field label="Datum" value={data.execution_date || data.requested_collection_date} />
              <Field
                label="Upload"
                value={
                  <span className="inline-flex items-center gap-1">
                    {uploadIcon(data.upload_status)}
                    {data.upload_status}
                  </span>
                }
              />
              <Field label="Reconciliation" value={reconciliationBadge(data.reconciliation_status)} />
              {data.last_pain002_at && (
                <Field label="Letzte pain.002" value={new Date(data.last_pain002_at).toLocaleString("de-CH")} />
              )}
              {data.last_pain002_chdd_at && (
                <Field label="Letzte pain.002-chdd" value={new Date(data.last_pain002_chdd_at).toLocaleString("de-CH")} />
              )}
              {data.upload_error && (
                <div className="md:col-span-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-rose-900">
                  <strong>Upload-Fehler:</strong> {data.upload_error}
                </div>
              )}
            </div>

            <div className="border rounded-md overflow-hidden">
              <div className="bg-slate-100 px-3 py-2 text-xs font-medium border-b">
                {lineLabel} ({lines.length})
              </div>
              <ul className="divide-y text-xs" data-testid="batch-detail-lines">
                {lines.map((l, i) => (
                  <li key={i} className="px-3 py-2 flex items-center justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-mono">
                        {l.invoice_number || l.bill_number || l.end_to_end_id || "—"}
                      </div>
                      <div className="text-[10.5px] text-slate-500 truncate">
                        {l.creditor_name || l.debtor_name || ""}
                        {(l.creditor_iban || l.debtor_iban) && (
                          <span className="font-mono ml-1">
                            · {l.creditor_iban || l.debtor_iban}
                          </span>
                        )}
                      </div>
                      {(l.invoice_status || l.bill_status) && (
                        <div className="text-[10px] mt-0.5">
                          Status:{" "}
                          <span className="font-medium">
                            {l.invoice_status || l.bill_status}
                          </span>
                          {l.invoice_paid_via === "lsv" && (
                            <Badge className="ml-1 bg-emerald-100 text-emerald-800 text-[9px]">
                              LSV bezahlt
                            </Badge>
                          )}
                          {l.invoice_rejected_reason && (
                            <Badge className="ml-1 bg-rose-100 text-rose-800 text-[9px]">
                              {l.invoice_rejected_reason.substring(0, 40)}
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="font-medium text-right">
                      {(l.amount || 0).toFixed(2)} {l.currency || "CHF"}
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {data.skipped_invoices && data.skipped_invoices.length > 0 && (
              <div className="text-xs">
                <strong>Übersprungen:</strong>{" "}
                {data.skipped_invoices.map((s, i) => (
                  <span key={i} className="text-slate-500">
                    {s.invoice_number || s.invoice_id?.substring(0, 8)} ({s.reason})
                    {i < data.skipped_invoices.length - 1 ? " · " : ""}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, mono = false }) {
  return (
    <div>
      <div className="text-[10.5px] text-slate-500">{label}</div>
      <div className={`text-xs ${mono ? "font-mono" : "font-medium"}`}>{value || "—"}</div>
    </div>
  );
}
